import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useEmpresa } from '../contexts/EmpresaContext'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import NaturezaSelect from '../components/NaturezaSelect'
import ContaSelect from '../components/ContaSelect'
import CentroCustoSelect from '../components/CentroCustoSelect'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'
import { registrarGuardaSaida, saidaBloqueada } from '../utils/guardaSaida'
import { avisosPeriodo, PeriodoCompras } from '../utils/periodoCompras'

// Comprador desativado não aparece na busca do lançamento
const SOMENTE_ATIVOS = { ativos: '1' }

// Tela dedicada de lançamento de CT-e modelo 57 (frete comprado de transportadora) —
// equivalente enxuto da Lançamento de Notas (NF-e 55), só com os campos que fazem
// sentido pra um serviço de transporte: sem CFOP redundante, sem pneu/veículo/OS,
// com Natureza/Financeiro pré-preenchidos pelos parâmetros de importação de CT-e
// (frota.parcte, GET /erp/cte57/parametros/:empcod) e um item único representando
// o frete. Grava exatamente nos mesmos endpoints/tabelas que a tela de NF-e já usa
// (frota.ntvcad via POST /nota-entrada, frota.ntvits via POST .../itens) — mesma
// integração contábil, sem tabela paralela.

interface Natureza {
  tno_cod: number; tno_cdf: number; tno_des: string; tno_dsc: string; tno_loc?: string; tno_ctp?: string
}

interface Parametros {
  empest: string
  docFinanceiro: { cod: string; des: string }
  contaFinanceira: { cod: string; des: string }
  contaAplicacao: { cod: string; des: string }
  centroCusto: { cod: string; des: string }
  destinoServico: { cod: string; des: string }
  codServico: { cod: number | null; des: string }
  naturezaEstadual: { cod: number; cdf: number; des: string } | null
  naturezaInterestadual: { cod: number; cdf: number; des: string } | null
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function parseNumBr(v: string) {
  const s = String(v ?? '').trim()
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return Number(s) || 0
}
function fmtMoeda(v: string) { return parseNumBr(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }
function round2(v: number) { return Math.round((v + Number.EPSILON) * 100) / 100 }
function somarDiasISO(dataISO: string, dias: number) {
  const d = new Date(dataISO + 'T00:00:00')
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}
function limitarAno4Digitos(valorAtual: string, valorNovo: string) {
  const ano = valorNovo.split('-')[0]
  return ano && ano.length > 4 ? valorAtual : valorNovo
}
function fmtCnpj(doc: string) {
  const n = (doc || '').replace(/\D/g, '')
  if (n.length !== 14) return doc || ''
  return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

const ESTILO_AUTO: React.CSSProperties = { background: '#fffbeb', borderColor: '#fbbf24', color: '#92400e', fontWeight: 700 }
const ESTILO_CALC: React.CSSProperties = { background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e', fontWeight: 700, cursor: 'not-allowed' }

function Secao({ titulo, badge, children }: { titulo: string; badge?: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
      borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '0.7rem',
    }}>
      <h4 style={{
        fontSize: '0.68rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.05em',
        marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
      }}>
        {titulo}
        {badge && <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 9999, padding: '1px 7px', textTransform: 'none', letterSpacing: 0 }}>{badge}</span>}
      </h4>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.8rem', alignItems: 'flex-end' }}>
        {children}
      </div>
    </div>
  )
}

function Divisor({ titulo }: { titulo: string }) {
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem', margin: '0.35rem 0 0.05rem' }}>
      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#8a97a8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{titulo}</span>
      <span style={{ flex: 1, height: 1, background: '#e2e8f0', borderRadius: 1 }} />
    </div>
  )
}

function Campo({ label, width, auto, children }: { label: string; width?: number | string; auto?: boolean; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width, gap: '0.15rem' }}>
      <label style={{ fontSize: '0.66rem', display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {auto && <span style={{ fontSize: '0.56rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 9999, padding: '1px 6px', textTransform: 'uppercase' }}>auto</span>}
      </label>
      {children}
    </div>
  )
}

export default function LancamentoCte57() {
  const { empresaCod, empresaAtual } = useEmpresa()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const importarChaveUrl = searchParams.get('importarChave') || ''
  const { cstDes, classTribDes } = useIbsCbsDescricoes()

  // Transportadora
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [fornecedorUf, setFornecedorUf] = useState('')
  const [fornecedorCidade, setFornecedorCidade] = useState('')
  const [fornecedorCnpj, setFornecedorCnpj] = useState('')
  const [fornecedorFj, setFornecedorFj] = useState('')
  const [fornecedorBloqueado, setFornecedorBloqueado] = useState(false)

  // Documento
  const [serie, setSerie] = useState('')
  const [numeroNota, setNumeroNota] = useState('')
  const [chaveCte, setChaveCte] = useState('')
  const [dataEmissao, setDataEmissao] = useState('')
  const [dataRecebimento, setDataRecebimento] = useState('')
  // Período da TPARCOM: avisa já ao digitar a data
  const [periodoCompras, setPeriodoCompras] = useState<PeriodoCompras | null>(null)
  const avisoPeriodo = avisosPeriodo(periodoCompras, dataEmissao, dataRecebimento)
  useEffect(() => {
    api.get(`/nota-entrada/periodo/${empresaCod ?? 1}`).then(r => setPeriodoCompras(r.data)).catch(() => setPeriodoCompras(null))
  }, [empresaCod])

  // Parâmetros de importação (TPARCTE)
  const [parametros, setParametros] = useState<Parametros | null>(null)
  const [erroParametros, setErroParametros] = useState('')

  // Natureza/Financeiro — auto-preenchidos, editáveis
  const [naturezas, setNaturezas] = useState<Natureza[]>([])
  const [naturezaCod, setNaturezaCod] = useState('')
  const [naturezaCdf, setNaturezaCdf] = useState('')
  const [naturezaDes, setNaturezaDes] = useState('')
  const [naturezaOrigem, setNaturezaOrigem] = useState<'estadual' | 'interestadual' | ''>('')

  const [tipoDocumento, setTipoDocumento] = useState('')
  const [tipoDocumentoDes, setTipoDocumentoDes] = useState('')
  const [contaFinanceira, setContaFinanceira] = useState('')
  const [contaFinanceiraDes, setContaFinanceiraDes] = useState('')
  const [contaAplicacaoCod, setContaAplicacaoCod] = useState('')
  const [contaAplicacaoDes, setContaAplicacaoDes] = useState('')
  const [centroCustoCod, setCentroCustoCod] = useState('')
  const [centroCustoDes, setCentroCustoDes] = useState('')
  const [destinoServicoCod, setDestinoServicoCod] = useState('')
  const [destinoServicoDes, setDestinoServicoDes] = useState('')
  const [codServico, setCodServico] = useState<number | null>(null)
  const [codServicoDes, setCodServicoDes] = useState('')

  // Cobrança
  const [compradorCod, setCompradorCod] = useState('')
  const [compradorDes, setCompradorDes] = useState('')
  const [nrFatura, setNrFatura] = useState('')
  const [condicaoPagamento, setCondicaoPagamento] = useState('')
  const [condicaoPagamentoDes, setCondicaoPagamentoDes] = useState('')

  // Retenções e Ajustes (mesmos campos de ntvcad que a NF-e já usa)
  const [valorIrrf, setValorIrrf] = useState('0,00')
  const [percIrrf, setPercIrrf] = useState('0,00')
  const [valorPisRet, setValorPisRet] = useState('0,00')
  const [valorCofinsRet, setValorCofinsRet] = useState('0,00')
  const [valorCsll, setValorCsll] = useState('0,00')
  const [valorDescontoRs, setValorDescontoRs] = useState('0,00')
  const [valorSeguro, setValorSeguro] = useState('0,00')
  const [valorAcrescimo, setValorAcrescimo] = useState('0,00')

  // Prestação do Serviço
  const [valorPrestacao, setValorPrestacao] = useState('0,00')
  const [trajeto, setTrajeto] = useState('')
  const [observacao, setObservacao] = useState('')

  // Tributos do item único (a prestação de frete)
  const [icmsBase, setIcmsBase] = useState('0,00')
  const [icmsPerc, setIcmsPerc] = useState('0,00')
  const [icmsValor, setIcmsValor] = useState('0,00')
  const [icmsCst, setIcmsCst] = useState('')
  const [pisBase, setPisBase] = useState('0,00')
  const [pisPerc, setPisPerc] = useState('0,00')
  const [pisValor, setPisValor] = useState('0,00')
  const [pisCst, setPisCst] = useState('')
  const [cofinsBase, setCofinsBase] = useState('0,00')
  const [cofinsPerc, setCofinsPerc] = useState('0,00')
  const [cofinsValor, setCofinsValor] = useState('0,00')
  const [cofinsCst, setCofinsCst] = useState('')
  const [inssBase, setInssBase] = useState('0,00')
  const [inssPerc, setInssPerc] = useState('0,00')
  const [inssValor, setInssValor] = useState('0,00')
  const [difal, setDifal] = useState('0,00')

  // IBS/CBS — manual por enquanto (bug conhecido: importação de XML de CT-e não lê o
  // grupo IBSCBS ainda, só a de NF-e)
  const [ibsCbsCst, setIbsCbsCst] = useState('')
  const [ibsCbsClassTrib, setIbsCbsClassTrib] = useState('')
  const [cbsBase, setCbsBase] = useState('0,00')
  const [cbsBcEf, setCbsBcEf] = useState('0,00')
  const [cbsAliRef, setCbsAliRef] = useState('0,00')
  const [cbsRedAli, setCbsRedAli] = useState('0,00')
  const [cbsAliEf, setCbsAliEf] = useState('0,00')
  const [cbsValor, setCbsValor] = useState('0,00')
  const [ibsUfBcEf, setIbsUfBcEf] = useState('0,00')
  const [ibsUfAliRef, setIbsUfAliRef] = useState('0,00')
  const [ibsUfRedAli, setIbsUfRedAli] = useState('0,00')
  const [ibsUfAliEf, setIbsUfAliEf] = useState('0,00')
  const [ibsUfValor, setIbsUfValor] = useState('0,00')
  const [ibsMunBase, setIbsMunBase] = useState('0,00')
  const [ibsMunBcEf, setIbsMunBcEf] = useState('0,00')
  const [ibsMunAliRef, setIbsMunAliRef] = useState('0,00')
  const [ibsMunRedAli, setIbsMunRedAli] = useState('0,00')
  const [ibsMunAliEf, setIbsMunAliEf] = useState('0,00')
  const [ibsMunValor, setIbsMunValor] = useState('0,00')

  const [salvando, setSalvando] = useState(false)
  const [erroSalvar, setErroSalvar] = useState('')
  const [notaSalva, setNotaSalva] = useState<{ empcod: number; serie: string; numero: number } | null>(null)

  // Financeiro (parcelas) — fica direto na tela, não em modal separado
  const [parcelas, setParcelas] = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [financeiroSalvo, setFinanceiroSalvo] = useState(false)

  useEffect(() => {
    api.get('/erp/naturezas', { params: { tipo: 'entrada', csa: 'S' } }).then(r => setNaturezas(r.data)).catch(() => {})
  }, [])

  // Busca os parâmetros de importação de CT-e (TPARCTE) assim que souber a empresa.
  useEffect(() => {
    if (!empresaCod) return
    setErroParametros('')
    api.get(`/erp/cte57/parametros/${empresaCod}`).then(r => {
      const p: Parametros = r.data
      setParametros(p)
      setTipoDocumento(p.docFinanceiro.cod); setTipoDocumentoDes(p.docFinanceiro.des)
      setContaFinanceira(p.contaFinanceira.cod); setContaFinanceiraDes(p.contaFinanceira.des)
      setContaAplicacaoCod(p.contaAplicacao.cod); setContaAplicacaoDes(p.contaAplicacao.des)
      setCentroCustoCod(p.centroCusto.cod); setCentroCustoDes(p.centroCusto.des)
      setDestinoServicoCod(p.destinoServico.cod); setDestinoServicoDes(p.destinoServico.des)
      setCodServico(p.codServico.cod); setCodServicoDes(p.codServico.des)
    }).catch(err => {
      setParametros(null)
      setErroParametros(err?.response?.data?.error || 'Não foi possível carregar os parâmetros de importação de CT-e (frota.parcte) desta empresa.')
    })
  }, [empresaCod])

  // Decide Natureza Estadual x Interestadual comparando a UF da transportadora com a
  // UF da empresa — mesma regra do xmlBuilderCteOS.ts pro CT-e OS (TAF x Registro
  // Estadual), só que aqui é Natureza da Operação em vez de campo do XML.
  useEffect(() => {
    if (!parametros || !fornecedorUf) return
    const interestadual = fornecedorUf.trim().toUpperCase() !== parametros.empest.trim().toUpperCase()
    const escolhida = interestadual ? parametros.naturezaInterestadual : parametros.naturezaEstadual
    if (escolhida) {
      setNaturezaCod(String(escolhida.cod))
      setNaturezaCdf(String(escolhida.cdf))
      setNaturezaDes(escolhida.des)
      setNaturezaOrigem(interestadual ? 'interestadual' : 'estadual')
    } else {
      setNaturezaCod(''); setNaturezaCdf(''); setNaturezaDes('')
      setNaturezaOrigem('')
    }
  }, [parametros, fornecedorUf])

  async function onSelecionarFornecedor(cod: string, des: string, item: any) {
    setFornecedorCod(cod); setFornecedorDes(des)
    setFornecedorBloqueado(!!item?.bloqueado)
    setFornecedorFj(item?.cli_fj || '')
    setFornecedorUf(item?.end_uf || '')
    setFornecedorCidade(item?.end_cidade || '')
    setFornecedorCnpj(item?.cli_mat || '')
  }

  // Importação de XML (vindo do Manifestador de CT-e ou da tela "Importar CT-e",
  // ambos passam ?importarChave= na URL) — mesma origem de dados que o Lançamento
  // de Notas usa (/erp/notas-entrada/importar-xml), só que aqui só existe um item
  // (o frete) e a gente já sabe de antemão que vem de um CT-e.
  const [importandoXml, setImportandoXml] = useState(false)
  const [avisoImportarXml, setAvisoImportarXml] = useState('')
  const [fornecedorPendenteXml, setFornecedorPendenteXml] = useState<{ chave: string; cliMat: string; cliNom: string } | null>(null)

  async function importarDoXml(chave: string, criarFornecedor?: boolean) {
    setImportandoXml(true); setAvisoImportarXml('')
    try {
      const r = await api.get('/erp/notas-entrada/importar-xml', {
        params: { chave, empcod: empresaCod ?? 1, ...(criarFornecedor ? { criarFornecedor: 1 } : {}) },
      })
      const d = r.data

      if (d.fornecedorPendente) {
        setFornecedorPendenteXml({ chave, cliMat: d.fornecedorPendente.cliMat, cliNom: d.fornecedorPendente.cliNom })
        setImportandoXml(false)
        return
      }
      setFornecedorPendenteXml(null)

      if (d.fornecedor.criadoAgora) window.open(`/configuracoes/clientes?editar=${d.fornecedor.cliCod}`, '_blank')

      try {
        const rc = await api.get('/erp/clientes/lista', { params: { busca: d.fornecedor.cliCod } })
        const item = (rc.data || []).find((x: any) => x.cli_cod === d.fornecedor.cliCod) || rc.data?.[0]
        if (item) await onSelecionarFornecedor(item.cli_cod, item.cli_fan || item.cli_nom, item)
        else { setFornecedorCod(d.fornecedor.cliCod); setFornecedorDes(d.fornecedor.cliNom) }
      } catch {
        setFornecedorCod(d.fornecedor.cliCod); setFornecedorDes(d.fornecedor.cliNom)
      }

      setChaveCte(chave)
      setSerie(d.capa.serie)
      setNumeroNota(String(d.capa.numero))
      if (!nrFatura) setNrFatura(String(d.capa.numero))
      if (d.capa.dataEmissao) setDataEmissao(d.capa.dataEmissao)
      if (d.capa.observacao) setObservacao(d.capa.observacao)
      setValorSeguro(fmtMoeda(String(d.capa.valorSeguro)))
      setValorDescontoRs(fmtMoeda(String(d.capa.valorDescontoRs)))
      setValorAcrescimo(fmtMoeda(String(d.capa.valorAcrescimo)))
      setValorIrrf(fmtMoeda(String(d.capa.valorIrrf)))
      setValorPisRet(fmtMoeda(String(d.capa.valorPis)))
      setValorCofinsRet(fmtMoeda(String(d.capa.valorCofins)))
      setValorCsll(fmtMoeda(String(d.capa.valorCsll)))
      setValorPrestacao(fmtMoeda(String(d.capa.valorTotal)))

      const item = d.itens?.[0]
      if (item) {
        setTrajeto(item.descComplementar || '')
        setIcmsBase(fmtMoeda(String(item.icmsBase))); setIcmsPerc(fmtMoeda(String(item.icmsPerc)))
        setIcmsValor(fmtMoeda(String(item.icmsValor))); setIcmsCst(item.icmsCst || '')
        setIbsCbsCst(item.ibsCbsCst || ''); setIbsCbsClassTrib(item.ibsCbsClassTrib || '')
        setCbsBase(fmtMoeda(String(item.cbsBase))); setCbsBcEf(fmtMoeda(String(item.cbsBcEf)))
        setCbsAliRef(fmtMoeda(String(item.cbsAliRef))); setCbsRedAli(fmtMoeda(String(item.cbsRedAli)))
        setCbsAliEf(fmtMoeda(String(item.cbsAliEf))); setCbsValor(fmtMoeda(String(item.cbsValor)))
        setIbsUfBcEf(fmtMoeda(String(item.ibsUfBcEf))); setIbsUfAliRef(fmtMoeda(String(item.ibsUfAliRef)))
        setIbsUfRedAli(fmtMoeda(String(item.ibsUfRedAli))); setIbsUfAliEf(fmtMoeda(String(item.ibsUfAliEf)))
        setIbsUfValor(fmtMoeda(String(item.ibsUfValor)))
        setIbsMunBase(fmtMoeda(String(item.ibsMunBase))); setIbsMunBcEf(fmtMoeda(String(item.ibsMunBcEf)))
        setIbsMunAliRef(fmtMoeda(String(item.ibsMunAliRef))); setIbsMunRedAli(fmtMoeda(String(item.ibsMunRedAli)))
        setIbsMunAliEf(fmtMoeda(String(item.ibsMunAliEf))); setIbsMunValor(fmtMoeda(String(item.ibsMunValor)))
      }

      setAvisoImportarXml((d.avisos || []).join(' '))
    } catch (e: any) {
      setAvisoImportarXml(e?.response?.data?.error || 'Erro ao importar XML.')
    } finally {
      setImportandoXml(false)
    }
  }

  function confirmarCadastroFornecedorXml() {
    if (!fornecedorPendenteXml) return
    importarDoXml(fornecedorPendenteXml.chave, true)
  }

  // Veio do Manifestador de CT-e ou da tela "Importar CT-e" com a chave na URL —
  // importa sozinho, sem o usuário ter que digitar a chave de novo.
  const importarUrlDisparado = useRef(false)
  useEffect(() => {
    if (!importarChaveUrl || importarUrlDisparado.current || !empresaAtual) return
    importarUrlDisparado.current = true
    importarDoXml(importarChaveUrl)
  }, [importarChaveUrl, empresaAtual])

  function onSelecionarNatureza(cod: string) {
    const n = naturezas.find(x => String(x.tno_cod) === cod)
    setNaturezaCod(cod)
    setNaturezaCdf(n ? String(n.tno_cdf) : '')
    setNaturezaDes(n?.tno_des || '')
    setNaturezaOrigem('') // escolha manual — sai do automático
  }

  // Recalcula ICMS/PIS/COFINS/INSS/DIFAL usando o mesmo motor de impostos da NF-e,
  // com o Cód.Produto/Serviço do parâmetro (74649 etc.) como "item" — é o mesmo
  // truque que o CT-e OS já faz (um item único representando o frete).
  useEffect(() => {
    if (!naturezaCod || !codServico || parseNumBr(valorPrestacao) <= 0) return
    const t = setTimeout(async () => {
      try {
        const r = await api.post('/erp/itens-nota/calcular-impostos', {
          empresaCod: empresaCod ?? 1,
          empresaUf: empresaAtual?.empest || '',
          tnocod: Number(naturezaCod),
          itemCod: codServico,
          clienteFj: fornecedorFj,
          clienteUf: fornecedorUf,
          valorMercadoria: parseNumBr(valorPrestacao),
          valorAcrescimo: 0, valorFrete: 0, valorSeguro: 0, valorDesconto: 0,
        })
        const d = r.data
        setIcmsCst(d.icms.cst); setIcmsBase(fmtMoeda(String(d.icms.base))); setIcmsPerc(fmtMoeda(String(d.icms.perc))); setIcmsValor(fmtMoeda(String(d.icms.valor)))
        setPisCst(d.pis.cst); setPisBase(fmtMoeda(String(d.pis.base))); setPisPerc(fmtMoeda(String(d.pis.perc))); setPisValor(fmtMoeda(String(d.pis.valor)))
        setCofinsCst(d.cofins.cst); setCofinsBase(fmtMoeda(String(d.cofins.base))); setCofinsPerc(fmtMoeda(String(d.cofins.perc))); setCofinsValor(fmtMoeda(String(d.cofins.valor)))
        setInssBase(fmtMoeda(String(d.inss.base))); setInssPerc(fmtMoeda(String(d.inss.perc))); setInssValor(fmtMoeda(String(d.inss.valor)))
        setDifal(fmtMoeda(String(d.difal.valor)))
      } catch { /* cálculo automático é best-effort; usuário ajusta na mão se falhar */ }
    }, 600)
    return () => clearTimeout(t)
  }, [naturezaCod, codServico, valorPrestacao, empresaCod, empresaAtual, fornecedorFj, fornecedorUf])

  const valorTotalNota = round2(
    parseNumBr(valorPrestacao) + parseNumBr(valorSeguro) + parseNumBr(valorAcrescimo) - parseNumBr(valorDescontoRs),
  )

  function validar(): string[] {
    const erros: string[] = []
    if (!fornecedorCod) erros.push('Transportadora')
    if (fornecedorBloqueado) erros.push('Transportadora está bloqueada')
    if (!dataEmissao) erros.push('Data de Emissão')
    if (dataEmissao > hojeISO()) erros.push('Data de emissão não pode ser maior que hoje')
    if (!dataRecebimento) erros.push('Data Recebimento')
    if (dataRecebimento > hojeISO()) erros.push('Data de recebimento não pode ser maior que hoje')
    if (dataEmissao && dataRecebimento && dataRecebimento < dataEmissao) erros.push('Data de recebimento anterior à emissão')
    if (avisoPeriodo.emissao) erros.push(avisoPeriodo.emissao)
    if (avisoPeriodo.recebimento) erros.push(avisoPeriodo.recebimento)
    if (!chaveCte.trim() || chaveCte.replace(/\D/g, '').length !== 44) erros.push('Chave de acesso do CT-e (44 dígitos)')
    if (!naturezaCod) erros.push('Natureza da Operação')
    if (!nrFatura) erros.push('Nr Fatura')
    if (!tipoDocumento) erros.push('Doc. Financeiro')
    if (!contaFinanceira) erros.push('Conta Financeira')
    if (!contaAplicacaoCod) erros.push('Conta Aplicação') // destino do serviço nunca é Estoque
    if (parseNumBr(valorPrestacao) <= 0) erros.push('Valor da Prestação')
    if (parseNumBr(valorIrrf) > 0 && !serie.trim()) erros.push('Série é obrigatória quando há retenção de INSS/IRRF')
    return erros
  }

  async function salvar() {
    const erros = validar()
    if (erros.length) { setErroSalvar(`Preencha: ${erros.join(', ')}`); return }
    setErroSalvar(''); setSalvando(true)
    try {
      const numero = Number(numeroNota)
      const cabecalho = await api.post('/nota-entrada', {
        empcod: empresaCod,
        serie,
        numero,
        cliCod: fornecedorCod,
        dataEmissao,
        dataRecebimento,
        naturezaCod: Number(naturezaCod),
        naturezaCdf: naturezaCdf ? Number(naturezaCdf) : undefined,
        naturezaDes,
        observacao,
        compradorCod: compradorCod ? Number(compradorCod) : undefined,
        contaFinanceira: contaFinanceira || undefined,
        ftvNumero: Number(nrFatura),
        ftvTipdco: tipoDocumento,
        modelo: '57',
        chaveNfe: chaveCte,
        condicaoPagamento: condicaoPagamento ? Number(condicaoPagamento) : undefined,
        valorDescontoRs: parseNumBr(valorDescontoRs),
        valorSeguro: parseNumBr(valorSeguro),
        valorAcrescimo: parseNumBr(valorAcrescimo),
        tipoFrete: 9,
        valorFrete: 0,
        valorIrrf: parseNumBr(valorIrrf),
        percIrrf: parseNumBr(percIrrf),
        valorInss: parseNumBr(inssValor),
        valorIssqn: 0,
        percIss: 0,
        valorPis: fornecedorFj !== 'F' ? parseNumBr(valorPisRet) : 0,
        valorCofins: fornecedorFj !== 'F' ? parseNumBr(valorCofinsRet) : 0,
        valorCsll: fornecedorFj !== 'F' ? parseNumBr(valorCsll) : 0,
        baseIcmsSt: 0,
        valorIcmsSt: 0,
        uf: fornecedorUf,
        valorMercadoria: parseNumBr(valorPrestacao),
        valorTotal: valorTotalNota,
      })
      const salva = cabecalho.data as { empcod: number; serie: string; numero: number }
      // Item já gravado só vale se for a mesma nota (usuário pode ter trocado número/série entre tentativas)
      const mesmaNota = !!notaSalva && notaSalva.empcod === salva.empcod && notaSalva.serie === salva.serie && notaSalva.numero === salva.numero
      const linhaGravada = mesmaNota ? linhaItem : null
      if (!mesmaNota) setLinhaItem(null)
      setNotaSalva(salva)

      const payloadItem = {
        itemCod: codServico, itemDes: codServicoDes,
        descComplementar: [trajeto].filter(Boolean).join(' — ').slice(0, 120),
        itemUn: 'UN', itemTipo: 'S',
        quantidade: 1, valorTotal: parseNumBr(valorPrestacao),
        origemCod: 'FOR', destinoCod: destinoServicoCod,
        aplicacaoCod: contaAplicacaoCod,
        centroCustoCod, rateioCusto: '', numeroParcelas: null,
        ncmCod: '', servicoCod: '',
        itemValorFrete: 0, itemValorAcrescimo: 0, itemValorSeguro: 0, itemValorDesconto: 0,
        inssBase: parseNumBr(inssBase), inssPerc: parseNumBr(inssPerc), inssValor: parseNumBr(inssValor),
        icmsBase: parseNumBr(icmsBase), icmsPerc: parseNumBr(icmsPerc), icmsValor: parseNumBr(icmsValor), icmsCst,
        icmsStBase: 0, icmsStPerc: 0, icmsStValor: 0,
        ipiBase: 0, ipiPerc: 0, ipiValor: 0, ipiCst: '',
        pisBase: parseNumBr(pisBase), pisPerc: parseNumBr(pisPerc), pisValor: parseNumBr(pisValor), pisCst,
        cofinsBase: parseNumBr(cofinsBase), cofinsPerc: parseNumBr(cofinsPerc), cofinsValor: parseNumBr(cofinsValor), cofinsCst,
        difal: parseNumBr(difal),
        tnocod: Number(naturezaCod),
        ibsCbsCst, ibsCbsClassTrib,
        cbsBase: parseNumBr(cbsBase), cbsBcEf: parseNumBr(cbsBcEf), cbsAliRef: parseNumBr(cbsAliRef),
        cbsRedAli: parseNumBr(cbsRedAli), cbsAliEf: parseNumBr(cbsAliEf), cbsValor: parseNumBr(cbsValor),
        ibsUfBcEf: parseNumBr(ibsUfBcEf), ibsUfAliRef: parseNumBr(ibsUfAliRef),
        ibsUfRedAli: parseNumBr(ibsUfRedAli), ibsUfAliEf: parseNumBr(ibsUfAliEf), ibsUfValor: parseNumBr(ibsUfValor),
        ibsMunBase: parseNumBr(ibsMunBase), ibsMunBcEf: parseNumBr(ibsMunBcEf), ibsMunAliRef: parseNumBr(ibsMunAliRef),
        ibsMunRedAli: parseNumBr(ibsMunRedAli), ibsMunAliEf: parseNumBr(ibsMunAliEf), ibsMunValor: parseNumBr(ibsMunValor),
      }
      // Nova tentativa (ex.: financeiro falhou) atualiza o item já gravado em vez de duplicar
      const baseItens = `/erp/notas-entrada/${salva.empcod}/${salva.serie}/${salva.numero}/${fornecedorCod}/itens`
      if (linhaGravada != null) {
        await api.put(`${baseItens}/${linhaGravada}`, payloadItem)
      } else {
        const r = await api.post(baseItens, payloadItem)
        setLinhaItem(Number(r.data.linha))
      }

      if (!parcelasBatemComTotal) {
        setErroSalvar(`A soma das parcelas (R$ ${fmtMoeda(String(totalParcelas))}) precisa bater com o total (R$ ${fmtMoeda(String(valorTotalNota))}).`)
        return
      }
      await api.post('/erp/notas-entrada/financeiro', {
        empcod: salva.empcod, numero: salva.numero, serie: salva.serie, cliente: fornecedorCod, modelo: '57',
        dataEmissao, contaCod: contaFinanceira,
        parcelas: parcelas.map(p => ({ numero: p.numero, vencimento: p.vencimento, valor: parseNumBr(p.valor) })),
      })
      setFinanceiroSalvo(true)
    } catch (e: any) {
      setErroSalvar(e?.response?.data?.error || 'Erro ao salvar o lançamento de CT-e')
    } finally {
      setSalvando(false)
    }
  }

  // Parcelas do Financeiro — preenchidas sozinhas pela Condição de Pagamento (igual
  // o Lançamento de NF-e) sempre que Condição/Data/Total mudam, até o usuário mexer
  // numa parcela na mão (daí para de recalcular sozinho, pra não perder a edição).
  const [parcelasEditadas, setParcelasEditadas] = useState(false)

  async function gerarParcelasIniciais(total: number) {
    if (condicaoPagamento) {
      try {
        const r = await api.get(`/condicao-pagamento/${condicaoPagamento}`)
        const linhas: { num: number; dias: number; percentual: number }[] = r.data?.parcelas || []
        if (linhas.length) {
          let acumulado = 0
          return linhas.map((l, idx) => {
            let valor = round2(total * (l.percentual / 100))
            if (idx === linhas.length - 1) valor = round2(total - acumulado)
            else acumulado = round2(acumulado + valor)
            return { numero: String(l.num).padStart(2, '0'), vencimento: somarDiasISO(dataEmissao, l.dias), valor: fmtMoeda(String(valor)) }
          })
        }
      } catch { /* condição sem parcelas — cai no fallback */ }
    }
    return [{ numero: '01', vencimento: dataEmissao, valor: fmtMoeda(String(total)) }]
  }

  useEffect(() => {
    if (parcelasEditadas || parseNumBr(valorPrestacao) <= 0) return
    gerarParcelasIniciais(valorTotalNota).then(setParcelas)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condicaoPagamento, dataEmissao, valorTotalNota, parcelasEditadas])

  function alterarParcela(idx: number, campo: 'numero' | 'vencimento' | 'valor', valor: string) {
    setParcelasEditadas(true)
    setParcelas(list => list.map((p, i) => i === idx ? { ...p, [campo]: valor } : p))
  }
  function adicionarParcela() {
    setParcelasEditadas(true)
    setParcelas(list => [...list, { numero: String(list.length + 1).padStart(2, '0'), vencimento: dataEmissao, valor: '0,00' }])
  }
  function removerParcela(idx: number) {
    setParcelasEditadas(true)
    setParcelas(list => list.filter((_, i) => i !== idx))
  }

  const totalParcelas = parcelas.reduce((s, p) => s + parseNumBr(p.valor), 0)
  const parcelasBatemComTotal = Math.abs(totalParcelas - valorTotalNota) < 0.01

  // Capa gravada e o item falhou: segura a saída (menu, Cancelar, Sair, fechar aba) pra não deixar CT-e sem item
  const [linhaItem, setLinhaItem] = useState<number | null>(null)
  const [saidaPendente, setSaidaPendente] = useState<(() => void) | null>(null)
  const [excluindoCapa, setExcluindoCapa] = useState(false)
  const [erroExcluirCapa, setErroExcluirCapa] = useState('')
  const capaSemItens = !!notaSalva && linhaItem == null
  useEffect(() => {
    if (!capaSemItens) return
    const desfazer = registrarGuardaSaida(prosseguir => { setSaidaPendente(() => prosseguir); return true })
    const aoFechar = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', aoFechar)
    return () => { desfazer(); window.removeEventListener('beforeunload', aoFechar) }
  }, [capaSemItens])

  async function excluirCapaESair() {
    if (!notaSalva || !saidaPendente) return
    setExcluindoCapa(true); setErroExcluirCapa('')
    try {
      await api.post('/nota-entrada/excluir-sem-itens', {
        empcod: notaSalva.empcod, serie: notaSalva.serie, numero: notaSalva.numero, cliCod: fornecedorCod,
      })
      const prosseguir = saidaPendente
      setSaidaPendente(null); setNotaSalva(null)
      prosseguir()
    } catch (e: any) {
      setErroExcluirCapa(e?.response?.data?.error || 'Erro ao excluir o CT-e')
    } finally {
      setExcluindoCapa(false)
    }
  }

  function sairPara(rota: string) {
    const ir = () => navigate(rota)
    if (!saidaBloqueada(ir)) ir()
  }

  if (financeiroSalvo && notaSalva) {
    return (
      <div>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Lançamento CTe 57</h2>
        <div className="alert alert-success">
          CT-e {notaSalva.numero} lançado com sucesso (empresa {notaSalva.empcod}, série {notaSalva.serie}).
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem' }}>
          <button className="btn btn-primary btn-sm" onClick={() => window.location.reload()}>Lançar outro CT-e</button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/compras/consultas/notas')}>Ir para Consulta de Notas</button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Lançamento CTe 57</h2>

      {erroParametros && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erroParametros}</div>}

      {importandoXml && <div className="alert alert-warning" style={{ marginBottom: '0.75rem' }}>Importando CT-e do XML...</div>}

      {fornecedorPendenteXml && (
        <div className="alert alert-warning" style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span>Transportadora "{fornecedorPendenteXml.cliNom}" ({fmtCnpj(fornecedorPendenteXml.cliMat)}) não está cadastrada. Cadastrar automaticamente com os dados do XML?</span>
          <button className="btn btn-primary btn-sm" onClick={confirmarCadastroFornecedorXml}>Cadastrar e importar</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setFornecedorPendenteXml(null)}>Cancelar</button>
        </div>
      )}

      {avisoImportarXml && !fornecedorPendenteXml && (
        <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', color: '#1a6ea8', fontWeight: 600, fontSize: '0.82rem' }}>
          {avisoImportarXml || 'CT-e importado do XML — confira Natureza/Financeiro (preenchidos pelas regras) e os tributos antes de salvar.'}
        </div>
      )}

      <Secao titulo="Documento">
        <Campo label="Transportadora" width={380}>
          <LookupSelect
            endpoint="/erp/clientes/lista"
            codKey="cli_cod" descKey="cli_fan"
            codValue={fornecedorCod} descValue={fornecedorDes}
            valorExibido={fornecedorDes ? `${fmtCnpj(fornecedorCnpj)} - ${fornecedorDes}` : undefined}
            onSelect={onSelecionarFornecedor}
            placeholder="Buscar transportadora..." listWidth={320} somenteComTexto
          />
        </Campo>
        {fornecedorCod && (fornecedorCidade || fornecedorUf) && (
          <Campo label="Endereço">
            <div style={{ display: 'flex', alignItems: 'center', height: 30, fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
              {fornecedorCidade}{fornecedorCidade && fornecedorUf ? ' - ' : ''}{fornecedorUf}
            </div>
          </Campo>
        )}
        {fornecedorBloqueado && (
          <div className="alert alert-danger" style={{ width: '100%' }}>Esta transportadora está BLOQUEADA — não é possível lançar CT-e para ela.</div>
        )}
        <Campo label="Série" width={70}>
          <input type="text" value={serie} onChange={e => setSerie(e.target.value.replace(/\D/g, '').slice(0, 3))} />
        </Campo>
        <Campo label="Número" width={120}>
          <input type="text" value={numeroNota} onChange={e => setNumeroNota(e.target.value.replace(/\D/g, ''))} />
        </Campo>
        <Campo label="Chave de Acesso CT-e" width={340}>
          <input type="text" value={chaveCte} maxLength={44} style={{ fontFamily: 'monospace' }}
            onChange={e => setChaveCte(e.target.value.replace(/\D/g, ''))} />
        </Campo>
        <Campo label="Data de Emissão" width={150}>
          <input type="date" value={dataEmissao} onChange={e => setDataEmissao(limitarAno4Digitos(dataEmissao, e.target.value))}
            style={avisoPeriodo.emissao ? { outline: '2px solid #dc2626', borderColor: '#dc2626' } : undefined} />
        </Campo>
        <Campo label="Data Recebimento" width={150}>
          <input type="date" value={dataRecebimento} onChange={e => setDataRecebimento(limitarAno4Digitos(dataRecebimento, e.target.value))}
            style={avisoPeriodo.recebimento ? { outline: '2px solid #dc2626', borderColor: '#dc2626' } : undefined} />
        </Campo>
        {(avisoPeriodo.emissao || avisoPeriodo.recebimento) && (
          <div style={{ width: '100%', color: '#dc2626', fontSize: '0.75rem', fontWeight: 600 }}>
            {avisoPeriodo.emissao && <div>⚠ {avisoPeriodo.emissao}</div>}
            {avisoPeriodo.recebimento && <div>⚠ {avisoPeriodo.recebimento}</div>}
          </div>
        )}
        <Campo label="Modelo" width={110}>
          <input type="text" value="57 — CT-e" disabled />
        </Campo>
      </Secao>

      <Secao titulo="Natureza da Operação e Financeiro" badge="preenchido pelos parâmetros do estabelecimento">
        <Campo label="Doc. Financeiro" width={230} auto>
          <input type="text" value={tipoDocumento ? `${tipoDocumento} — ${tipoDocumentoDes}` : ''} style={ESTILO_AUTO} onChange={() => {}} readOnly />
        </Campo>
        <Campo label="Cta. Financeira" width={280}>
          <ContaSelect value={contaFinanceira} onChange={(cod, des) => { setContaFinanceira(cod); setContaFinanceiraDes(des) }} />
        </Campo>
        <Campo label="Cód. Produto/Serviço" width={230} auto>
          <input type="text" value={codServico ? `${codServico} — ${codServicoDes}` : ''} style={ESTILO_AUTO} readOnly onChange={() => {}} />
        </Campo>
        <Campo label="Destino do Serviço" width={210} auto>
          <input type="text" value={destinoServicoCod ? `${destinoServicoCod} — ${destinoServicoDes}` : ''} style={ESTILO_AUTO} readOnly onChange={() => {}} />
        </Campo>
        <Campo label="Conta Aplicação *" width={280}>
          <LookupSelect
            endpoint="/erp/aplicacoes" codKey="apl_cod" descKey="apl_des" tagKey="apl_cod"
            codValue={contaAplicacaoCod} descValue={contaAplicacaoDes}
            valorExibido={contaAplicacaoCod ? `${contaAplicacaoCod} - ${contaAplicacaoDes}` : ''}
            onSelect={(cod, des) => { setContaAplicacaoCod(cod); setContaAplicacaoDes(des) }}
            placeholder="Buscar aplicação..." listWidth={300}
          />
        </Campo>
        <Campo label="Centro de Custo" width={220}>
          <CentroCustoSelect value={centroCustoCod} onChange={(cod, des) => { setCentroCustoCod(cod); setCentroCustoDes(des) }} />
        </Campo>

        <Campo label="Natureza da Operação" width="100%">
          <NaturezaSelect naturezas={naturezas} value={naturezaCod} onChange={onSelecionarNatureza} />
          {naturezaOrigem && (
            <div style={{ fontSize: '0.68rem', color: '#92400e', marginTop: '0.25rem' }}>
              Aplicada automaticamente ({naturezaOrigem === 'estadual' ? 'Estadual' : 'Interestadual'}) porque a transportadora é
              {naturezaOrigem === 'estadual' ? ' do mesmo estado' : ' de outro estado'} da empresa. Ajustável se a operação for outra.
            </div>
          )}
        </Campo>

        <Divisor titulo="Cobrança" />
        <Campo label="Nº Fatura" width={150}>
          <input type="text" value={nrFatura} onChange={e => setNrFatura(e.target.value)} />
        </Campo>
        <Campo label="Condição de Pagamento" width={260}>
          <LookupSelect
            endpoint="/condicao-pagamento" codKey="tcv_cod" descKey="tcv_des" tagKey="tcv_cod"
            codValue={condicaoPagamento} descValue={condicaoPagamentoDes}
            onSelect={(cod, des) => { setCondicaoPagamento(cod); setCondicaoPagamentoDes(des) }}
            placeholder="Buscar condição de pagamento..." listWidth={320}
          />
        </Campo>
        <Campo label="Comprador" width={220}>
          <LookupSelect
            endpoint="/compradores" extraParams={SOMENTE_ATIVOS} codKey="cpr_cod" descKey="cpr_des"
            codValue={compradorCod} descValue={compradorDes}
            onSelect={(cod, des) => { setCompradorCod(cod); setCompradorDes(des) }}
            placeholder="Buscar comprador..." listWidth={260}
          />
        </Campo>

        <Divisor titulo="Retenções e Ajustes" />
        <Campo label="IRRF Valor" width={120}><input type="text" inputMode="decimal" value={valorIrrf} onChange={e => setValorIrrf(e.target.value)} /></Campo>
        <Campo label="IRRF %" width={90}><input type="text" inputMode="decimal" value={percIrrf} onChange={e => setPercIrrf(e.target.value)} /></Campo>
        {fornecedorFj !== 'F' && (
          <>
            <Campo label="PIS" width={120}><input type="text" inputMode="decimal" value={valorPisRet} onChange={e => setValorPisRet(e.target.value)} /></Campo>
            <Campo label="COFINS" width={120}><input type="text" inputMode="decimal" value={valorCofinsRet} onChange={e => setValorCofinsRet(e.target.value)} /></Campo>
            <Campo label="CSLL" width={120}><input type="text" inputMode="decimal" value={valorCsll} onChange={e => setValorCsll(e.target.value)} /></Campo>
          </>
        )}
        <Campo label="Desconto R$" width={120}><input type="text" inputMode="decimal" value={valorDescontoRs} onChange={e => setValorDescontoRs(e.target.value)} /></Campo>
        <Campo label="Seguro" width={120}><input type="text" inputMode="decimal" value={valorSeguro} onChange={e => setValorSeguro(e.target.value)} /></Campo>
        <Campo label="Acréscimo" width={120}><input type="text" inputMode="decimal" value={valorAcrescimo} onChange={e => setValorAcrescimo(e.target.value)} /></Campo>
      </Secao>

      <Secao titulo="Prestação do Serviço">
        <Campo label="Valor da Prestação (Frete)" width={180}>
          <input type="text" inputMode="decimal" value={valorPrestacao} onChange={e => setValorPrestacao(e.target.value)} />
        </Campo>
        <Campo label="Trajeto" width={420}>
          <input type="text" value={trajeto} onChange={e => setTrajeto(e.target.value)} placeholder="Origem → Destino" />
        </Campo>
        <Campo label="Observações / Complemento" width="100%">
          <textarea rows={2} value={observacao} onChange={e => setObservacao(e.target.value)} />
        </Campo>
      </Secao>

      <Secao titulo="Tributos">
        <Divisor titulo="ICMS / PIS / COFINS (calculado pela Natureza/CFOP)" />
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <table style={{ width: '100%', maxWidth: 820, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#1a6ea8' }}>
                {['Tributo', 'Base de Cálculo', 'Alíquota', 'Valor', 'CST'].map(h => (
                  <th key={h} style={{ padding: '5px', border: '1px solid #1a6ea8', color: '#fff', fontWeight: 700, fontSize: '0.66rem', textTransform: 'uppercase', textAlign: h === 'Tributo' ? 'left' : 'center', width: h === 'Tributo' ? 130 : undefined }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <LinhaTributo nome="ICMS" base={icmsBase} perc={icmsPerc} valor={icmsValor} cst={icmsCst} onCst={setIcmsCst} calc />
              <LinhaTributo nome="PIS" base={pisBase} perc={pisPerc} valor={pisValor} cst={pisCst} onCst={setPisCst} calc />
              <LinhaTributo nome="COFINS" base={cofinsBase} perc={cofinsPerc} valor={cofinsValor} cst={cofinsCst} onCst={setCofinsCst} calc />
            </tbody>
          </table>
        </div>

        <Divisor titulo="INSS Retido (quando aplicável)" />
        <Campo label="Base" width={140}><input type="text" inputMode="decimal" value={inssBase} onChange={e => setInssBase(e.target.value)} /></Campo>
        <Campo label="Alíquota" width={100}><input type="text" inputMode="decimal" value={inssPerc} onChange={e => setInssPerc(e.target.value)} /></Campo>
        <Campo label="Valor" width={140}><input type="text" inputMode="decimal" value={inssValor} onChange={e => setInssValor(e.target.value)} /></Campo>

        <Divisor titulo="IBS / CBS (Reforma Tributária)" />
        <Campo label="CST" width={70}><input type="text" value={ibsCbsCst} maxLength={3} style={{ fontFamily: 'monospace', textAlign: 'center' }} onChange={e => setIbsCbsCst(e.target.value.slice(0, 3))} /></Campo>
        <Campo label="cClassTrib" width={110}><input type="text" value={ibsCbsClassTrib} maxLength={6} style={{ fontFamily: 'monospace', textAlign: 'center' }} onChange={e => setIbsCbsClassTrib(e.target.value.slice(0, 6))} /></Campo>
        {(cstDes(ibsCbsCst) || classTribDes(ibsCbsCst, ibsCbsClassTrib)) && (
          <div style={{ width: '100%', fontSize: '0.76rem', color: '#374151', lineHeight: 1.4 }}>
            {cstDes(ibsCbsCst) && <div><strong>CST {ibsCbsCst}:</strong> {cstDes(ibsCbsCst)}</div>}
            {classTribDes(ibsCbsCst, ibsCbsClassTrib) && <div><strong>cClassTrib {ibsCbsClassTrib}:</strong> {classTribDes(ibsCbsCst, ibsCbsClassTrib)}</div>}
          </div>
        )}
        <div style={{ width: '100%', overflowX: 'auto', marginTop: '0.4rem' }}>
          <table style={{ width: '100%', maxWidth: 820, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#1a6ea8' }}>
                {['Tributo', 'Base Cálculo', 'BC Efetiva', 'Ali.Ref %', 'Red.Ali %', 'Ali.Efet %', 'Valor'].map(h => (
                  <th key={h} style={{ padding: '5px', border: '1px solid #1a6ea8', color: '#fff', fontWeight: 700, fontSize: '0.66rem', textTransform: 'uppercase', textAlign: h === 'Tributo' ? 'left' : 'center', width: h === 'Tributo' ? 100 : undefined }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <LinhaIbsCbs nome="CBS" base={cbsBase} setBase={setCbsBase} bcEf={cbsBcEf} setBcEf={setCbsBcEf} aliRef={cbsAliRef} setAliRef={setCbsAliRef} redAli={cbsRedAli} setRedAli={setCbsRedAli} aliEf={cbsAliEf} setAliEf={setCbsAliEf} valor={cbsValor} setValor={setCbsValor} />
              <LinhaIbsCbs nome="IBS UF" bcEf={ibsUfBcEf} setBcEf={setIbsUfBcEf} aliRef={ibsUfAliRef} setAliRef={setIbsUfAliRef} redAli={ibsUfRedAli} setRedAli={setIbsUfRedAli} aliEf={ibsUfAliEf} setAliEf={setIbsUfAliEf} valor={ibsUfValor} setValor={setIbsUfValor} />
              <LinhaIbsCbs nome="IBS Mun." base={ibsMunBase} setBase={setIbsMunBase} bcEf={ibsMunBcEf} setBcEf={setIbsMunBcEf} aliRef={ibsMunAliRef} setAliRef={setIbsMunAliRef} redAli={ibsMunRedAli} setRedAli={setIbsMunRedAli} aliEf={ibsMunAliEf} setAliEf={setIbsMunAliEf} valor={ibsMunValor} setValor={setIbsMunValor} />
            </tbody>
          </table>
        </div>
      </Secao>

      <Secao titulo="Totais (calculado pelo sistema)">
        <Campo label="Valor ICMS" width={170}><input type="text" value={`R$ ${icmsValor}`} disabled style={ESTILO_CALC} /></Campo>
        <Campo label="Valor IBS" width={170}><input type="text" value={`R$ ${fmtMoeda(String(parseNumBr(ibsUfValor) + parseNumBr(ibsMunValor)))}`} disabled style={ESTILO_CALC} /></Campo>
        <Campo label="Valor CBS" width={170}><input type="text" value={`R$ ${cbsValor}`} disabled style={ESTILO_CALC} /></Campo>
        <Campo label="Valor Total da Nota" width={190}><input type="text" value={`R$ ${fmtMoeda(String(valorTotalNota))}`} disabled style={{ ...ESTILO_CALC, fontSize: '0.95rem' }} /></Campo>
      </Secao>

      <Secao titulo="Financeiro — Parcelas" badge="gerado pela Condição de Pagamento — ajustável">
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <table style={{ width: '100%', maxWidth: 560, borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#1a6ea8' }}>
                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'left' }}>Nº</th>
                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'left' }}>Vencimento</th>
                <th style={{ color: '#fff', padding: '4px 6px', textAlign: 'right' }}>Valor</th>
                <th style={{ padding: '4px 6px', width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {parcelas.map((p, i) => (
                <tr key={i}>
                  <td style={{ padding: '4px 6px' }}><input type="text" value={p.numero} onChange={e => alterarParcela(i, 'numero', e.target.value)} style={{ width: 50 }} /></td>
                  <td style={{ padding: '4px 6px' }}><input type="date" value={p.vencimento} onChange={e => alterarParcela(i, 'vencimento', e.target.value)} /></td>
                  <td style={{ padding: '4px 6px' }}><input type="text" inputMode="decimal" value={p.valor} onChange={e => alterarParcela(i, 'valor', e.target.value)} style={{ textAlign: 'right' }} /></td>
                  <td style={{ padding: '4px 6px' }}><button className="row-action-btn row-action-btn--danger" onClick={() => removerParcela(i)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={adicionarParcela}>+ Parcela</button>
        <div style={{ width: '100%', fontSize: '0.82rem', color: parcelasBatemComTotal ? '#166534' : '#dc2626', fontWeight: 600 }}>
          Total das parcelas: R$ {fmtMoeda(String(totalParcelas))} {parcelasBatemComTotal ? '✓' : `(esperado R$ ${fmtMoeda(String(valorTotalNota))})`}
        </div>
      </Secao>

      {erroSalvar && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erroSalvar}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.5rem' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => sairPara('/compras/recebimentos/lancamento-notas')}>Cancelar</button>
        <button className="btn btn-primary btn-sm" disabled={salvando} onClick={salvar} style={{ fontWeight: 700, padding: '0.5rem 1rem', boxShadow: '0 2px 6px rgba(0,0,0,0.18)' }}>
          {salvando ? 'Salvando...' : 'Salvar Lançamento'}
        </button>
      </div>

      {saidaPendente && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 0.6rem', fontSize: '1rem', fontWeight: 700, color: '#b91c1c' }}>
              CT-e sem item
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.45 }}>
              O CT-e {notaSalva?.numero} foi salvo, mas o item não foi gravado. O lançamento não pode ficar sem item:
              corrija e clique em Salvar Lançamento de novo, ou exclua o CT-e para sair.
            </p>
            {erroExcluirCapa && (
              <div style={{ marginBottom: '0.8rem', fontSize: '0.8rem', color: '#b91c1c' }}>{erroExcluirCapa}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-ghost btn-sm" disabled={excluindoCapa}
                onClick={() => { setSaidaPendente(null); setErroExcluirCapa('') }}>
                Continuar lançando
              </button>
              <button disabled={excluindoCapa} onClick={excluirCapaESair}
                style={{ padding: '0.45rem 0.9rem', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {excluindoCapa ? 'Excluindo...' : 'Excluir o CT-e e sair'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function LinhaTributo({ nome, base, perc, valor, cst, onCst, calc }: {
  nome: string; base: string; perc: string; valor: string; cst: string; onCst: (v: string) => void; calc?: boolean
}) {
  const td: React.CSSProperties = { padding: '2px 5px', border: '1px solid #eef0f4' }
  const inp: React.CSSProperties = calc
    ? { width: '100%', height: 24, padding: '0 5px', fontSize: '0.74rem', borderRadius: 3, border: '1px solid #fbbf24', background: '#fffbeb', color: '#92400e', fontWeight: 700, textAlign: 'right', boxSizing: 'border-box', cursor: 'not-allowed' }
    : { width: '100%', height: 24, padding: '0 5px', fontSize: '0.74rem', borderRadius: 3, border: '1px solid #e2e8f0', textAlign: 'right', boxSizing: 'border-box' }
  return (
    <tr>
      <td style={{ ...td, fontWeight: 700 }}>{nome} {calc && <span style={{ fontSize: '0.56rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 9999, padding: '1px 6px', marginLeft: 4 }}>calc</span>}</td>
      <td style={td}><input readOnly value={base} style={inp} /></td>
      <td style={td}><input readOnly value={perc} style={inp} /></td>
      <td style={td}><input readOnly value={valor} style={inp} /></td>
      <td style={td}><input readOnly value={cst} style={{ ...inp, textAlign: 'center', fontFamily: 'monospace' }} onChange={e => onCst(e.target.value)} /></td>
    </tr>
  )
}

function LinhaIbsCbs({ nome, base, setBase, bcEf, setBcEf, aliRef, setAliRef, redAli, setRedAli, aliEf, setAliEf, valor, setValor }: {
  nome: string
  base?: string; setBase?: (v: string) => void
  bcEf?: string; setBcEf?: (v: string) => void
  aliRef?: string; setAliRef?: (v: string) => void
  redAli?: string; setRedAli?: (v: string) => void
  aliEf?: string; setAliEf?: (v: string) => void
  valor?: string; setValor?: (v: string) => void
}) {
  const td: React.CSSProperties = { padding: '2px 5px', border: '1px solid #eef0f4' }
  const inp: React.CSSProperties = { width: '100%', height: 24, padding: '0 5px', fontSize: '0.74rem', borderRadius: 3, border: '1px solid #e2e8f0', textAlign: 'right', boxSizing: 'border-box' }
  const vazio = <span style={{ color: '#d1d5db' }}>—</span>
  return (
    <tr>
      <td style={{ ...td, fontWeight: 700 }}>{nome}</td>
      <td style={td}>{setBase ? <input value={base} onChange={e => setBase(e.target.value)} style={inp} /> : vazio}</td>
      <td style={td}>{setBcEf ? <input value={bcEf} onChange={e => setBcEf(e.target.value)} style={inp} /> : vazio}</td>
      <td style={td}>{setAliRef ? <input value={aliRef} onChange={e => setAliRef(e.target.value)} style={inp} /> : vazio}</td>
      <td style={td}>{setRedAli ? <input value={redAli} onChange={e => setRedAli(e.target.value)} style={inp} /> : vazio}</td>
      <td style={td}>{setAliEf ? <input value={aliEf} onChange={e => setAliEf(e.target.value)} style={inp} /> : vazio}</td>
      <td style={td}>{setValor ? <input value={valor} onChange={e => setValor(e.target.value)} style={inp} /> : vazio}</td>
    </tr>
  )
}
